// src/quiz/mba-mock4/Mba4intro.js - Instruction page for "MBA Mock Test 4"
// Route: /quiz/mba-mock4   (Start Test -> /quiz/mba-mock4/test, Exit -> /)
// Uses react-router-dom (Create React App). Reads all numbers from ./mba4data.

import React from "react";
import { useNavigate } from "react-router-dom";
import { mba4Meta } from "./mba4data";

const GREEN = "#16a34a";
const DARK_GREEN = "#166534";

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f3faf6",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    // top padding clears the fixed site navbar so the title is not hidden behind it
    padding: "112px 16px 56px",
    boxSizing: "border-box",
    fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    color: "#1f2937",
  },
  card: {
    width: "100%",
    maxWidth: 780,
    background: "#ffffff",
    borderRadius: 16,
    boxShadow: "0 4px 24px rgba(22, 101, 52, 0.08)",
    border: "1px solid #e6f4ec",
    padding: "30px 34px 28px",
    boxSizing: "border-box",
  },
  title: {
    textAlign: "center",
    fontSize: 28,
    fontWeight: 800,
    color: DARK_GREEN,
    margin: 0,
    letterSpacing: "-0.3px",
  },
  subtitle: {
    textAlign: "center",
    color: "#6b7280",
    fontSize: 14,
    margin: "10px 0 22px",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
    marginBottom: 24,
  },
  statBox: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 12,
    padding: "14px 10px",
    textAlign: "center",
  },
  statNum: { fontSize: 22, fontWeight: 800, color: GREEN, lineHeight: 1.1 },
  statLabel: { fontSize: 12, color: "#6b7280", marginTop: 5 },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: DARK_GREEN,
    fontSize: 16,
    fontWeight: 700,
    margin: "0 0 10px",
  },
  box: {
    border: "1px solid #d9e2dd",
    borderRadius: 12,
    padding: "2px 20px",
    marginBottom: 22,
    background: "#ffffff",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 0",
    fontSize: 13.5,
    color: "#374151",
    gap: 16,
  },
  rowQs: { fontWeight: 700, color: DARK_GREEN, whiteSpace: "nowrap" },
  divider: { borderTop: "1px solid #eef2ef" },
  bullet: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "10px 0",
    fontSize: 13.5,
    lineHeight: 1.5,
    color: "#374151",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: GREEN,
    flexShrink: 0,
    marginTop: 7,
  },
  btnRow: { display: "flex", justifyContent: "center", gap: 12, marginTop: 4, flexWrap: "wrap" },
  startBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: GREEN,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "11px 26px",
    fontSize: 14.5,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(22, 163, 74, 0.3)",
  },
  exitBtn: {
    background: "#fff",
    color: "#1f2937",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "11px 28px",
    fontSize: 14.5,
    fontWeight: 700,
    cursor: "pointer",
  },
};

/* ---- small inline icons (no extra dependency needed) ---- */
const iconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};
const FileIcon = () => (
  <svg {...iconProps}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6M9 17h6" />
  </svg>
);
const ClockIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const PlayIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <polygon points="6 4 20 12 6 20 6 4" />
  </svg>
);

export default function Mba4Intro({
  startHref = "/quiz/mba-mock4/test",
  exitHref = "/",
  onStart,
  onExit,
}) {
  const navigate = useNavigate();
  const {
    title,
    subtitle,
    totalQuestions,
    totalMarks,
    durationMinutes,
    sections,
    negativeMarkPerWrong,
  } = mba4Meta;

  const handleStart = () => (onStart ? onStart() : navigate(startHref));
  const handleExit = () => (onExit ? onExit() : navigate(exitHref));

  const markingLine =
    negativeMarkPerWrong > 0
      ? `Each wrong answer carries a penalty of ${negativeMarkPerWrong} mark.`
      : "No negative marking.";

  const conditions = [
    `Marks are given per question as shown in the section breakdown. ${markingLine}`,
    `One timer runs for the entire ${durationMinutes}-minute paper.`,
    "All questions of a section are shown together on one page - scroll down to see them all.",
    "Move between sections with the Previous section and Next section buttons. The question palette on the right shows the current section; click a number to jump to that question.",
    "Each question has four options - select only one answer per question. Use \"Clear response\" to remove your answer.",
    "Use the flag button on a question to mark it for review and revisit it before submitting.",
    "Your answers and the timer are saved if you refresh the page.",
    "Use of an electronic calculator is not permitted.",
    "The test auto-submits when the timer reaches zero.",
  ];

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>{title}</h1>
        <p style={styles.subtitle}>{subtitle}</p>

        {/* Stat boxes */}
        <div style={styles.statsRow}>
          <div style={styles.statBox}>
            <div style={styles.statNum}>{totalQuestions}</div>
            <div style={styles.statLabel}>Questions</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statNum}>{totalMarks}</div>
            <div style={styles.statLabel}>Marks</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statNum}>{durationMinutes}</div>
            <div style={styles.statLabel}>Minutes</div>
          </div>
        </div>

        {/* Section breakdown */}
        <h2 style={styles.heading}>
          <FileIcon /> Section Breakdown
        </h2>
        <div style={styles.box}>
          {sections.map((s, i) => (
            <div key={s.id} style={{ ...styles.row, ...(i > 0 ? styles.divider : {}) }}>
              <span>
                {s.name}
                <span style={{ color: "#9ca3af", fontSize: 12 }}>
                  {"  "}({s.marksPerQuestion} mark{s.marksPerQuestion > 1 ? "s" : ""} each)
                </span>
              </span>
              <span style={styles.rowQs}>{s.count} Qs</span>
            </div>
          ))}
        </div>

        {/* Exam conditions */}
        <h2 style={styles.heading}>
          <ClockIcon /> Exam Conditions
        </h2>
        <div style={styles.box}>
          {conditions.map((c, i) => (
            <div key={i} style={{ ...styles.bullet, ...(i > 0 ? styles.divider : {}) }}>
              <span style={styles.dot} />
              <span>{c}</span>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div style={styles.btnRow}>
          <button style={styles.startBtn} onClick={handleStart}>
            <PlayIcon /> Start Test
          </button>
          <button style={styles.exitBtn} onClick={handleExit}>
            Exit
          </button>
        </div>
      </div>
    </div>
  );
}